import { z } from "zod";
import { HelpArticleId, HelpScreen } from "./help.js";
import { Product } from "./product.js";

/**
 * 更新履歴とヘルプの追従（#204、docs/83）。ここ1か所が正本：
 *   - 更新履歴の1件（リポジトリの docs/changelog/<YYYYMMDD_HHMM>_<名前>.md）の形と読み方
 *     ＝変更を作った Claude Code がコミットと一緒に書く。書き忘れ・形の間違いは scripts/check-changelog.sh が止める（help/src/build/changelog.ts）
 *   - 区分・対象・製品の言葉（管理アプリ「更新履歴」と、利用者の「更新情報」で同じ）
 *   - 利用者の「更新情報」の API（GET /updates・POST /updates/seen）の形と画面の文（UPDATES_MSG）
 * 公開するかは運営が管理アプリで決める（★3。公開するまで利用者には出ない）
 */

/** 区分（★7） */
export const RELEASE_KINDS = {
  feature: "新機能",
  improve: "機能改善",
  change: "変更",
  fix: "不具合修正",
  other: "その他",
} as const;
export type ReleaseKind = keyof typeof RELEASE_KINDS;
export const ReleaseKind = z.enum(Object.keys(RELEASE_KINDS) as [ReleaseKind, ...ReleaseKind[]]);

/** 製品：現場力／営業力／両方／管理アプリ（ops＝利用者に見せない。管理アプリの一覧にだけ出る） */
export const RELEASE_PRODUCTS = { genba: "現場力", eigyo: "営業力", both: "両製品", ops: "管理アプリ" } as const;
export type ReleaseProduct = keyof typeof RELEASE_PRODUCTS;
export const ReleaseProduct = z.enum(Object.keys(RELEASE_PRODUCTS) as [ReleaseProduct, ...ReleaseProduct[]]);

/** 対象：利用者に見せる候補（公開は運営が決める）／サイレント（見せない・理由を書く）／運営だけ */
export const RELEASE_AUDIENCES = { users: "利用者候補", silent: "サイレント", ops: "運営" } as const;
export type ReleaseAudience = keyof typeof RELEASE_AUDIENCES;
export const ReleaseAudience = z.enum(Object.keys(RELEASE_AUDIENCES) as [ReleaseAudience, ...ReleaseAudience[]]);

/** 1件の題・本文の長さ（製品 DB の release_notes の CHECK と同じ） */
export const RELEASE_LIMITS = { titleMax: 60, bodyMax: 400, bodySentences: 3, screensMax: 20 } as const;

/** 置き場とファイル名（docs/changelog/20261018_0130_updates.md。名前は半角小文字・数字・_・-） */
export const CHANGELOG_DIR = "docs/changelog";
export const CHANGELOG_FILE_RE = /^(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})_[a-z0-9][a-z0-9_-]*\.md$/;

/** 1件の頭（front matter）。知らない項目は止める */
export const ChangelogFront = z
  .object({
    product: ReleaseProduct,
    kind: ReleaseKind,
    /** product: ops のときは ops（省けば ops） */
    audience: ReleaseAudience.optional(),
    title: z.string().trim().min(1, "題が空です").max(RELEASE_LIMITS.titleMax, `題は ${RELEASE_LIMITS.titleMax}字まで`),
    screens: z.array(HelpScreen).max(RELEASE_LIMITS.screensMax).default([]),
    /** 誘導するヘルプの記事（任意。公開の記事でなくてよい） */
    help: z.union([HelpArticleId, z.literal("")]).optional(),
    /** docs/16 の番号（"#200"） */
    issue: z.union([z.string().regex(/^#\d{1,5}$/, "issue は \"#200\" の形で"), z.literal("")]).optional(),
    /** 問い合わせの受付番号（S-00012。docs/53） */
    ticket: z.union([z.string().regex(/^S-\d{5}$/, "ticket は S-00012 の形で"), z.literal("")]).optional(),
    /** audience が silent のときだけ必須 */
    silent_reason: z.string().max(200).optional(),
  })
  .strict();
export type ChangelogFront = z.infer<typeof ChangelogFront>;

export type ChangelogEntry = {
  /** ファイル名（鍵。管理 DB の release_notes.file） */
  file: string;
  front: ChangelogFront;
  /** 対象（product: ops は ops） */
  audience: ReleaseAudience;
  body: string;
  /** ファイル名の日時（日本時間）。date＝YYYY-MM-DD・at＝ISO */
  date: string;
  at: string;
};

/** ファイル名の日時（日本時間）。形が違えば null */
export function changelogDateOf(file: string): { date: string; at: string } | null {
  const m = CHANGELOG_FILE_RE.exec(file);
  if (!m) return null;
  const [, y, mo, d, hh, mm] = m;
  const date = `${y}-${mo}-${d}`;
  const at = `${date}T${hh}:${mm}:00+09:00`;
  return Number.isNaN(Date.parse(at)) ? null : { date, at };
}

/** 値の閉じる位置（"…" の中の ] や \" は数えない）。配列は最初の ]（入れ子は書かない）、文字は閉じる " */
function closingOf(v: string): number {
  let inStr = v.startsWith('"');
  for (let i = 1; i < v.length; i++) {
    const c = v[i];
    if (inStr) {
      if (c === "\\") i++;
      else if (c === '"') {
        if (!v.startsWith("[")) return i;
        inStr = false;
      }
    } else if (c === '"') inStr = true;
    else if (c === "]") return i;
  }
  return -1;
}

/**
 * 頭（--- で囲んだ所）と本文に分ける。頭は「名前: 値」の1行ずつ（値は JSON の配列・"…" の文字列・そのままの文字）。
 * 値の後ろの「 # …」は説明として読まない（docs/83 §2 の見本の形）。YAML の全部は読まない（決まった形だけ）
 */
export function splitChangelog(src: string): { front: Record<string, unknown>; body: string } {
  const m = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/.exec(src);
  if (!m) throw new Error("頭（--- で囲んだ所）がありません");
  const front: Record<string, unknown> = {};
  for (const raw of (m[1] ?? "").split(/\r?\n/)) {
    const line = raw.trimEnd();
    if (!line.trim() || line.trim().startsWith("#")) continue;
    const kv = /^([a-z_]+):\s*(.*)$/.exec(line);
    if (!kv) throw new Error(`頭の行が読めません：「${line.slice(0, 40)}」`);
    const key = kv[1]!;
    let v = (kv[2] ?? "").trim();
    let value: unknown;
    if (v.startsWith("[") || v.startsWith('"')) {
      // 閉じたあとの説明（# …）を外す
      const close = closingOf(v);
      if (close < 0) throw new Error(`頭の ${key} の値が閉じていません`);
      const rest = v.slice(close + 1).trim();
      if (rest && !rest.startsWith("#")) throw new Error(`頭の ${key} の値の後ろが読めません`);
      v = v.slice(0, close + 1);
      try {
        value = JSON.parse(v);
      } catch {
        throw new Error(`頭の ${key} の値が読めません：「${v.slice(0, 40)}」`);
      }
    } else {
      value = v.replace(/\s+#.*$/, "").trim();
    }
    if (key in front) throw new Error(`頭の ${key} が2つあります`);
    front[key] = value;
  }
  return { front, body: (m[2] ?? "").replace(/\r\n/g, "\n").trim() };
}

/** 1件を読む（形が違えば ZodError か Error）。silent の理由・ops の対象もここで確かめる */
export function parseChangelog(file: string, src: string): ChangelogEntry {
  const when = changelogDateOf(file);
  if (!when) throw new Error("ファイル名は YYYYMMDD_HHMM_名前.md の形で（名前は半角小文字・数字・_・-）");
  const { front: raw, body } = splitChangelog(src);
  const front = ChangelogFront.parse(raw);
  const audience: ReleaseAudience = front.product === "ops" ? "ops" : (front.audience ?? "users");
  if (front.product === "ops" && front.audience && front.audience !== "ops") throw new Error("product: ops の対象は ops だけです");
  if (front.product !== "ops" && !front.audience) throw new Error("audience（users／silent／ops）がありません");
  if (audience === "silent" && !front.silent_reason?.trim()) throw new Error("audience: silent のときは silent_reason（見せない理由）が要ります");
  if (audience !== "silent" && front.silent_reason?.trim()) throw new Error("silent_reason は audience: silent のときだけ書きます");
  if (!body) throw new Error("本文がありません（利用者向けの言葉で 1〜3文）");
  if ([...body].length > RELEASE_LIMITS.bodyMax) throw new Error(`本文は ${RELEASE_LIMITS.bodyMax}字までです`);
  return { file, front, audience, body, ...when };
}

/** 画面のパスが重なるか（同じパス・どちらかが /* でその下）。更新履歴の1件の screens と記事の screens をくらべる */
export function screensOverlap(a: string, b: string): boolean {
  const c = (s: string) => s.replace(/\/+$/, "") || "/";
  const A = c(a);
  const B = c(b);
  if (A === B) return true;
  const under = (p: string, q: string) => p.endsWith("/*") && (q === (p.slice(0, -2) || "/") || q.startsWith(p.slice(0, -2) + "/"));
  return under(A, B) || under(B, A);
}

/** その1件が入る製品（両方は2つ。ops は無し） */
export const productsOfRelease = (p: ReleaseProduct): Product[] => (p === "both" ? ["genba", "eigyo"] : p === "ops" ? [] : [p]);

// ---------------------------------------------------------------------------
// 利用者の「更新情報」（★4：左の列・☰ の「お知らせ」の窓と朱の点・/updates。帯・メール・プッシュは無し）
// #206（2026-10-18、docs/83 §14）：前は「ヘルプ」の窓のいちばん上の段だった＝ヘルプとサポートと混ざって見づらいので「お知らせ」に分けた
// ---------------------------------------------------------------------------

/** 1件（公開済み。言葉のキーは製品の既定で埋めた文） */
export const UpdateItem = z.object({
  id: z.string(),
  kind: ReleaseKind,
  title: z.string(),
  body: z.string(),
  /** 誘導するヘルプの記事（/help/<id>/）。公開の記事かは画面が help-index で確かめる（下書きのうちは出さない） */
  help: z.object({ id: HelpArticleId, url: z.string() }).nullable(),
  publishedAt: z.string(),
  /** その人の未読か（ログイン前は false） */
  isNew: z.boolean(),
});
export type UpdateItem = z.infer<typeof UpdateItem>;

/** GET /updates。unread＝その人が最後に見たときより新しい件数（ログイン前は 0） */
export const UpdatesResponse = z.object({
  items: z.array(UpdateItem),
  unread: z.number().int().min(0),
  /** その人が最後に見た公開日時（ログイン前・まだ見ていなければ null） */
  seenAt: z.string().nullable(),
});
export type UpdatesResponse = z.infer<typeof UpdatesResponse>;

/** 「お知らせ」の窓に並べる数（最新から。古いものは「更新情報の一覧」＝/updates） */
export const UPDATES_IN_DIALOG = 10;
/** /updates に並べる数（最新から。古いものは並べない） */
export const UPDATES_PAGE_MAX = 200;

export const UPDATES_MSG = {
  title: "更新情報",
  /** 左の列・☰ の項目の名前と、窓の題（#206） */
  nav: "お知らせ",
  /** 公開の更新が0件のときの窓の1行（#206） */
  none: "新しいお知らせはありません",
  /** 「お知らせ」の読み上げ名（未読があれば数も。点は色と形だけなので数はここで伝える） */
  navLabel: (n: number) => (n > 0 ? `お知らせ（新しい更新 ${n}件）` : "お知らせ"),
  newMark: "新着",
  all: "更新情報の一覧",
  openHelp: "ヘルプで見る",
  back: "一覧へ",
  empty: "更新情報はまだありません。",
  month: (y: number, m: number) => `${y}年${m}月`,
  date: (iso: string) => {
    const d = new Date(new Date(iso).getTime() + 9 * 3_600_000);
    return `${d.getUTCFullYear()}/${String(d.getUTCMonth() + 1).padStart(2, "0")}/${String(d.getUTCDate()).padStart(2, "0")}`;
  },
  loadFailed: "更新情報を読み込めませんでした。",
} as const;

/** 製品ごとの /updates の頭の1文（製品名で） */
export const updatesLeadOf = (productName: string): string => `${productName}の機能の追加と改善のお知らせです。`;

// ---------------------------------------------------------------------------
// 配備の知らせ（FS の fs-vps-01-deploy.sh の終わり → 管理サーバーの受け口 POST /intake/deploy。問い合わせと同じ HMAC の署名）
// ---------------------------------------------------------------------------

/** kind "deploy"：配備したコミットと、そのときリポジトリにある更新履歴の1件（ファイル名）。管理アプリが配備日を入れる */
export const IntakeDeploy = z.object({
  kind: z.literal("deploy"),
  commit: z.string().regex(/^[0-9a-f]{7,40}$/),
  products: z.array(Product).min(1).max(2),
  files: z.array(z.string().regex(CHANGELOG_FILE_RE)).max(20000),
  at: z.iso.datetime({ offset: true }),
});
export type IntakeDeploy = z.infer<typeof IntakeDeploy>;
